import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Factory } from "lucide-react";
import type { OrderLineRecord } from "@/types";
import { fmtDay, qty, type OrderProduction } from "@/types/production";
import { WorkOrderStatusBadge } from "./ProductionBadges";
import { WorkOrderFormDialog, type LinePreset } from "./WorkOrderFormDialog";

/** What is being made for an order: its work orders, and how much of each line is planned. Make one from a line. */
export function OrderProductionCard({ orderId, orderNumber, lines, cancelled }: { orderId: string; orderNumber: string; lines: OrderLineRecord[]; cancelled: boolean }) {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("production.create");
  const [data, setData] = useState<OrderProduction | null>(null);
  const [preset, setPreset] = useState<LinePreset | null>(null);

  const load = useCallback(() => {
    api.get(`/api/WorkOrder/ForOrder/${orderId}`).then((r) => setData(unwrapObject<OrderProduction>(r))).catch(() => setData(null));
  }, [orderId]);
  useEffect(load, [load]);

  if (!data) return null;
  const remainingOf = (lineId: string) => data.lines.find((l) => l.order_line_id === lineId);
  const makeable = lines.filter((l) => l.product_id && canCreate && !cancelled);
  if (!data.work_orders.length && !makeable.length) return null;

  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-bold flex items-center gap-2">
          <Factory className="size-4 text-muted-foreground" /> Production
        </h3>
        <div className="flex gap-1.5 flex-wrap">
          {makeable.map((l) => {
            const rem = remainingOf(l.id);
            return (
              <Button
                key={l.id}
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setPreset({ id: l.id, product_id: l.product_id!, product_name: l.product_name, variant_name: l.variant_name, unit: l.unit, remaining: rem?.remaining_qty ?? Number(l.quantity), delivery_date: l.delivery_date, order_number: orderNumber })}
              >
                Make {makeable.length > 1 ? l.product_name : "goods"}
                {rem && rem.remaining_qty > 0 ? ` (${rem.remaining_qty.toLocaleString("en-IN")} ${l.unit} unplanned)` : ""}
              </Button>
            );
          })}
        </div>
      </div>
      {data.work_orders.length === 0 ? (
        <p className="px-4 py-5 text-sm text-muted-foreground">Nothing is being made for this order yet. If it is not coming from stock, make a work order for it.</p>
      ) : (
        <ul className="divide-y">
          {data.work_orders.map((w) => (
            <li key={w.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap text-sm">
              <div>
                <Link to={`/production/${w.id}`} className="font-mono font-semibold text-primary hover:underline">{w.wo_number}</Link>
                <span className="ml-2">{w.product_name}{w.variant_name ? ` · ${w.variant_name}` : ""} · {qty(w.planned_qty, w.unit)}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{w.current_stage_name ? `${w.current_stage_name} · ` : ""}{w.stages_done}/{w.stages_total} stages</span>
                <span className={w.late ? "text-rose-600 font-semibold" : ""}>Due {fmtDay(w.due_date)}</span>
                <WorkOrderStatusBadge status={w.work_order_status} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <WorkOrderFormDialog open={!!preset} line={preset} onClose={() => setPreset(null)} onSaved={load} />
    </div>
  );
}
